class AdawatCarousel extends HTMLElement {
  connectedCallback() {
    if (this._adawatBooted) return;
    const boot = () => this.boot();
    if (window.AdawatLazyInit?.whenVisible) {
      window.AdawatLazyInit.whenVisible(this, boot);
    } else {
      boot();
    }
  }

  boot() {
    if (this._adawatBooted) return;
    this._adawatBooted = true;

    this.viewport = this.querySelector('[data-carousel-viewport]') || this.querySelector('.adawat-carousel__track-wrap');
    this.track = this.querySelector('[data-carousel-track]');
    if (this.hasAttribute('data-ten-picks')) {
      this.querySelectorAll('[data-carousel-slide]').forEach((slide) => {
        const n = Number(slide.querySelector('[data-price-dollars]')?.getAttribute('data-price-dollars') || '');
        if (Number.isFinite(n) && n > 10.0001) slide.remove();
      });
    }
    this.shuffleIfNeeded();
    this.originalSlides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.slides = this.originalSlides;
    this.prevBtn = this.querySelector('[data-carousel-prev]');
    this.nextBtn = this.querySelector('[data-carousel-next]');
    this.progressBar = this.querySelector('[data-carousel-progress]');

    if (!this.viewport || !this.track || this.slides.length === 0) return;

    this.mqMobile = window.matchMedia('(max-width: 767px)');
    this._skipNormalize = false;
    this.looping = false;

    if (this.wantsMobileLoop()) this.buildLoop();

    this.dragging = false;
    this.dragMoved = false;
    this.suppressClick = false;
    this.axisLocked = null;
    this.startX = 0;
    this.startY = 0;
    this.startScroll = 0;
    this.pointerId = null;
    this.dragThreshold = 12;

    this.prevBtn?.addEventListener('click', () => this.scrollByDir(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollByDir(1));
    this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.onResize = () => this.syncMode();
    window.addEventListener('resize', this.onResize);
    this.mqMobile.addEventListener?.('change', this.onResize);

    this._onPointerMove = (event) => this.onPointerMove(event);
    this._onPointerUp = (event) => this.onPointerUp(event);

    this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));

    // Block navigation only after a real drag — not on a normal product click
    this.track.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
        this.dragMoved = false;
      },
      true
    );

    this.track.style.transform = '';
    this.syncSlideMetrics();
    this._resizeObserver = new ResizeObserver(() => this.syncSlideMetrics());
    this._resizeObserver.observe(this.viewport);
    this.syncMode();
  }

  shuffleIfNeeded() {
    if (!this.hasAttribute('data-shuffle') || !this.track) return;

    const promo = this.track.querySelector('.adawat-carousel__slide--promo');
    const slides = Array.from(this.track.querySelectorAll('[data-carousel-slide]:not(.adawat-carousel__slide--promo)'));
    if (slides.length < 2) return;

    for (let i = slides.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const current = slides[i];
      slides[i] = slides[j];
      slides[j] = current;
    }

    const keep = Math.max(1, Number(this.getAttribute('data-shuffle-count')) || slides.length);
    slides.forEach((slide, index) => {
      if (index < keep) this.track.appendChild(slide);
      else slide.remove();
    });

    if (promo) this.track.prepend(promo);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.mqMobile?.removeEventListener?.('change', this.onResize);
    this._resizeObserver?.disconnect();
  }

  wantsMobileLoop() {
    return (
      this.closest('.adawat-product-carousel--mobile-carousel') &&
      this.originalSlides.length > 1
    );
  }

  isMobile() {
    return this.mqMobile ? this.mqMobile.matches : window.innerWidth <= 767;
  }

  isMobileCarousel() {
    return this.wantsMobileLoop() && this.isMobile();
  }

  isMobileGrid() {
    return this.isMobile() && !this.closest('.adawat-product-carousel--mobile-carousel');
  }

  buildLoop() {
    if (this.track.querySelector('[data-carousel-clone]')) {
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.looping = true;
      return;
    }

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.originalSlides.forEach((slide) => {
      const prev = slide.cloneNode(true);
      const next = slide.cloneNode(true);
      prev.setAttribute('data-carousel-clone', 'before');
      next.setAttribute('data-carousel-clone', 'after');
      prev.removeAttribute('data-carousel-slide');
      next.removeAttribute('data-carousel-slide');
      prev.classList.add('adawat-carousel__slide');
      next.classList.add('adawat-carousel__slide');
      prev.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      next.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      before.appendChild(prev);
      after.appendChild(next);
    });

    this.track.insertBefore(before, this.track.firstChild);
    this.track.appendChild(after);
    this.slides = Array.from(this.track.querySelectorAll('.adawat-carousel__slide'));
    this.looping = true;
  }

  setLoopWidth() {
    if (!this.originalSlides.length) return 0;
    const first = this.originalSlides[0];
    const last = this.originalSlides[this.originalSlides.length - 1];
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
    return last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
  }

  jumpToMiddle() {
    if (!this.looping || !this.isMobileCarousel()) return;
    const width = this.setLoopWidth();
    if (!width) return;
    this._skipNormalize = true;
    this.viewport.scrollLeft = width;
    requestAnimationFrame(() => {
      this._skipNormalize = false;
      this.updateUI();
    });
  }

  normalizeLoop() {
    if (!this.looping || this._skipNormalize || !this.isMobileCarousel()) return;
    const width = this.setLoopWidth();
    if (!width) return;

    const left = this.viewport.scrollLeft;
    if (left >= width * 2 - 4) {
      this._skipNormalize = true;
      this.viewport.scrollLeft = left - width;
      this._skipNormalize = false;
    } else if (left <= 4) {
      this._skipNormalize = true;
      this.viewport.scrollLeft = left + width;
      this._skipNormalize = false;
    }
  }

  syncMode() {
    this.syncSlideMetrics();
    if (this.looping) {
      if (this.isMobileCarousel()) {
        this.jumpToMiddle();
      } else {
        this._skipNormalize = true;
        this.viewport.scrollLeft = 0;
        requestAnimationFrame(() => {
          this._skipNormalize = false;
          this.updateUI();
        });
        return;
      }
    }
    this.updateUI();
  }

  bindWindowDragListeners() {
    window.addEventListener('pointermove', this._onPointerMove, { passive: false });
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
  }

  unbindWindowDragListeners() {
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  }

  canScroll() {
    return !this.isMobileGrid() && this.getMaxScroll() > 2;
  }

  syncSlideMetrics() {
    if (!this.viewport || !this.track) return;
    if (this.isMobileGrid()) {
      this.style.removeProperty('--adawat-vw');
      this.track.style.width = '';
      return;
    }

    const vw = Math.round(this.viewport.clientWidth);
    if (vw <= 0) return;
    this.style.setProperty('--adawat-vw', `${vw}px`);

    window.requestAnimationFrame(() => {
      if (!this.track) return;
      const slides = Array.from(this.track.children).filter((el) => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.hasAttribute('data-carousel-clone') && !this.isMobileCarousel()) return false;
        return el.offsetWidth > 0 && getComputedStyle(el).display !== 'none';
      });
      if (!slides.length) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap) || 0;
      const contentWidth = slides.reduce((sum, slide, index) => sum + slide.offsetWidth + (index ? gap : 0), 0);
      this.track.style.width = `${Math.ceil(contentWidth)}px`;
      this.updateUI();
    });
  }

  getStep() {
    if (this.isMobileGrid()) return 0;
    const productSlide =
      this.slides.find((slide) => !slide.classList.contains('adawat-carousel__slide--promo')) ||
      this.slides[0];
    if (!productSlide) return 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 16;
    return productSlide.offsetWidth + gap;
  }

  getMaxScroll() {
    return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
  }

  scrollByDir(direction) {
    const step = this.getStep();
    if (!step) return;
    const isRtl = document.documentElement.dir === 'rtl';
    const delta = (isRtl ? -direction : direction) * step;
    this.viewport.scrollBy({ left: delta, behavior: 'smooth' });
  }

  onScroll() {
    this.normalizeLoop();
    this.updateUI();
  }

  updateUI() {
    const max = this.getMaxScroll();
    const left = Math.abs(this.viewport.scrollLeft);
    const edge = 2;
    const loopingMobile = this.isMobileCarousel();

    if (this.prevBtn) this.prevBtn.disabled = loopingMobile ? false : left <= edge;
    if (this.nextBtn) this.nextBtn.disabled = loopingMobile ? false : left >= max - edge;

    if (this.progressBar) {
      let pct;
      if (loopingMobile) {
        const width = this.setLoopWidth();
        const pos = width ? ((left - width + width * 10) % width) / width : 0;
        pct = pos * 100;
      } else {
        pct = max <= 0 ? 100 : (left / max) * 100;
      }
      this.progressBar.style.width = `${Math.max(8, Math.min(100, pct || 8))}%`;
    }
  }

  onPointerDown(event) {
    if (!this.canScroll()) return;
    // Touch/pen: native overflow scroll so vertical page scroll works.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('button, .adawat-carousel__btn, input, label, select, textarea')) {
      return;
    }

    this.dragging = true;
    this.dragMoved = false;
    this.suppressClick = false;
    this.axisLocked = null;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startScroll = this.viewport.scrollLeft;
    this.pointerId = event.pointerId;
    // Do NOT setPointerCapture / is-dragging here — that blocks product <a> clicks.
    this.bindWindowDragListeners();
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    const threshold = this.dragThreshold;

    if (this.axisLocked == null) {
      if (Math.abs(dx) < threshold && Math.abs(dy) < threshold) return;
      this.axisLocked = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (this.axisLocked === 'y') {
        this.endDrag({ keepSuppress: false });
        return;
      }
    }

    if (this.axisLocked !== 'x') return;

    if (!this.dragMoved && Math.abs(dx) >= threshold) {
      this.dragMoved = true;
      this.suppressClick = true;
      this.viewport.classList.add('is-grabbing', 'is-dragging');
    }

    if (!this.dragMoved) return;

    this.viewport.scrollLeft = this.startScroll - dx;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragging) return;
    if (this.pointerId != null && event.pointerId !== this.pointerId) return;

    const didDrag = this.dragMoved;
    this.endDrag({ keepSuppress: didDrag });

    if (didDrag) {
      // Keep suppressClick true long enough for the trailing click event, then clear
      window.setTimeout(() => {
        this.suppressClick = false;
        this.dragMoved = false;
      }, 50);
    }
  }

  endDrag({ keepSuppress }) {
    this.dragging = false;
    this.axisLocked = null;
    this.pointerId = null;
    this.viewport.classList.remove('is-dragging', 'is-grabbing');
    this.unbindWindowDragListeners();
    if (!keepSuppress) {
      this.suppressClick = false;
      this.dragMoved = false;
    }
    this.updateUI();
  }
}

if (!customElements.get('adawat-carousel')) {
  customElements.define('adawat-carousel', AdawatCarousel);
}

function initAdawatCollectionTabs(root) {
  if (root.dataset.tabsReady === 'true') return;
  root.dataset.tabsReady = 'true';

  const tabs = Array.from(root.querySelectorAll('[data-tab]'));
  const panels = Array.from(root.querySelectorAll('[data-tab-panel]'));
  const heading = root.querySelector('[data-tab-heading]');
  const subheading = root.querySelector('[data-tab-subheading]');
  const viewMore = root.querySelector('[data-tab-view-more]');
  if (!tabs.length || !panels.length) return;

  const activate = (tab) => {
    const id = tab.dataset.tab;
    tabs.forEach((btn) => {
      const on = btn === tab;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const on = panel.dataset.tabPanel === id;
      panel.hidden = !on;
      panel.classList.toggle('is-active', on);
      if (!on) return;

      const viewport = panel.querySelector('[data-carousel-viewport]');
      if (viewport) viewport.scrollLeft = 0;
      const carousel = panel.querySelector('adawat-carousel');
      if (carousel && typeof carousel.syncSlideMetrics === 'function') {
        requestAnimationFrame(() => carousel.syncSlideMetrics());
      } else if (carousel && typeof carousel.updateUI === 'function') {
        requestAnimationFrame(() => carousel.updateUI());
      }
    });

    if (heading && tab.dataset.heading != null) {
      heading.textContent = tab.dataset.heading;
    }
    if (subheading) {
      const text = tab.dataset.subheading || '';
      subheading.textContent = text;
      subheading.hidden = !text;
    }
    if (viewMore && tab.dataset.viewUrl) {
      viewMore.setAttribute('href', tab.dataset.viewUrl);
    }
  };

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => activate(tab));
  });
}

document.querySelectorAll('[data-adawat-tabs]').forEach(initAdawatCollectionTabs);
document.addEventListener('shopify:section:load', (event) => {
  const root = event.target?.querySelector?.('[data-adawat-tabs]') || event.target;
  if (root?.hasAttribute?.('data-adawat-tabs')) initAdawatCollectionTabs(root);
});
